import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, FileText, GitCompareArrows, History, Loader2, Mail, MessageSquare, ScrollText, Send, Paperclip } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { statusLabels, statusColors, formatDateTime } from "../lib/contractUtils";
import { ContractHeader } from "../components/contract-detail/ContractHeader";
import { WorkflowStepper } from "../components/contract-detail/WorkflowStepper";
import { ContractSidebar } from "../components/contract-detail/ContractSidebar";
import {
  SignatureDialog, WorkflowDialog, ActionDialog, AddendumDialog, DeleteContractDialog,
} from "../components/contract-detail/ContractDialogs";
import { ExecutiveSummaryCard } from "../components/contract-detail/ExecutiveSummaryCard";
import { AddendaTab } from "../components/contract-detail/AddendaTab";
import { ShareDraftDialog, AddendumWorkflowDialog } from "../components/contract-detail/DetailDialogs";
import { AddendumPreviewDialog } from "../components/contract-detail/AddendumPreviewDialog";
import { VersionCompareDialog } from "../components/contract-detail/VersionCompareDialog";
import { NoticesTab } from "../components/contract-detail/NoticesTab";
import { AccessControlPanel } from "../components/contract-detail/AccessControlPanel";
import { CommentsPanel } from "../components/contract-detail/CommentsPanel";
import { PrintSignDialog } from "../components/contract-detail/PrintSignDialog";
import { DocumentsGallery } from "../components/contract-detail/DocumentsGallery";
import { LifecycleProgressBar } from "../components/contract-detail/LifecycleProgressBar";

export default function ContractDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "content";

  const [contract, setContract] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [shareDraftDialogOpen, setShareDraftDialogOpen] = useState(false);
  const [shareDraftData, setShareDraftData] = useState({ recipient_email: "", recipient_name: "", message: "" });
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [signatureData, setSignatureData] = useState({ name: "", email: "" });
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [externalReviewers, setExternalReviewers] = useState([]);
  const [workflowDueDate, setWorkflowDueDate] = useState("");
  const [policySuggestions, setPolicySuggestions] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionComment, setActionComment] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [printSignDialogOpen, setPrintSignDialogOpen] = useState(false);
  const [esignConfigured, setEsignConfigured] = useState(false);

  // Addendum state
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);
  const [addenda, setAddenda] = useState([]);
  const [addendumSaving, setAddendumSaving] = useState(false);
  const [addendumForm, setAddendumForm] = useState({
    addendum_type: "amendment", title: "", description: "", changes_summary: "", effective_date: "", new_expiration_date: "", new_value: "", template_id: null, sections: null,
  });
  const [addendumWorkflows, setAddendumWorkflows] = useState({});
  const [addendumWorkflowDialogOpen, setAddendumWorkflowDialogOpen] = useState(false);
  const [selectedAddendumId, setSelectedAddendumId] = useState(null);
  const [addendumReviewers, setAddendumReviewers] = useState([]);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [addendumApprovers, setAddendumApprovers] = useState([]);
  const [previewAddendum, setPreviewAddendum] = useState(null);
  const [compareVersions, setCompareVersions] = useState(null);

  useEffect(() => { fetchContract(); fetchUsers(); fetchAddenda(); fetchEsignStatus(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEsignStatus = async () => {
    try {
      const res = await apiClient.get("/organization/esign-status");
      setEsignConfigured(res.data.is_configured);
    } catch { /* silent */ }
  };

  const fetchContract = async () => {
    try {
      const [contractRes, workflowRes] = await Promise.all([
        apiClient.get(`/contracts/${id}`),
        apiClient.get(`/governed-workflows?contract_id=${id}`),
      ]);
      setContract(contractRes.data);
      setWorkflow(workflowRes.data.length > 0 ? workflowRes.data[0] : null);
      if (contractRes.data.executive_summary) setExecutiveSummary(contractRes.data.executive_summary);
    } catch { toast.error("Failed to load contract"); navigate("/contracts"); }
    finally { setLoading(false); }
  };

  const fetchAddenda = async () => {
    try {
      const resp = await apiClient.get(`/contracts/${id}/addenda`);
      setAddenda(resp.data);
      const wfMap = {};
      for (const a of resp.data) {
        try {
          const wfResp = await apiClient.get(`/addendum-workflows?addendum_id=${a.id}`);
          if (wfResp.data.length > 0) wfMap[a.id] = wfResp.data[0];
        } catch { /* ok */ }
      }
      setAddendumWorkflows(wfMap);
    } catch { /* ok */ }
  };

  const fetchUsers = async () => {
    try { const response = await apiClient.get("/users"); setUsers(response.data); } catch { /* ok */ }
  };

  // Handlers
  const handleArchive = async () => {
    try { await apiClient.post(`/contracts/${id}/archive`); toast.success("Contract archived"); fetchContract(); }
    catch { toast.error("Failed to archive contract"); }
  };

  const handleDelete = async () => {
    try { await apiClient.delete(`/contracts/${id}`); toast.success("Contract deleted"); navigate("/contracts"); }
    catch (error) { toast.error(error.response?.data?.detail || "Failed to delete contract"); }
    finally { setDeleteDialogOpen(false); }
  };

  const handleRequestSignature = async () => {
    try {
      await apiClient.post("/signatures/request", { contract_id: id, signer_name: signatureData.name, signer_email: signatureData.email });
      toast.success("Signature requested. Sign the contract to send it to the supplier for counter-signature.");
      setSignatureDialogOpen(false); fetchContract();
    } catch { toast.error("Failed to request signature"); }
  };

  const handleSign = async () => {
    if (!contract?.signature_request_id) { toast.error("No signature request found"); return; }
    try {
      const res = await apiClient.post(`/signatures/${contract.signature_request_id}/sign`);
      toast.success(res.data.status === "awaiting_counter_signature" ? "Contract signed! Counter-signature request sent to supplier." : "Contract signed and fully executed.");
      fetchContract();
    } catch { toast.error("Failed to sign contract"); }
  };

  const handleShareDraft = async () => {
    if (!shareDraftData.recipient_email) { toast.error("Please enter a recipient email"); return; }
    try {
      await apiClient.post(`/signatures/share-draft/${id}`, shareDraftData);
      toast.success(`Draft PDF sent to ${shareDraftData.recipient_email} (mock)`);
      setShareDraftDialogOpen(false); setShareDraftData({ recipient_email: "", recipient_name: "", message: "" }); fetchContract();
    } catch { toast.error("Failed to share draft"); }
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await apiClient.get(`/signatures/pdf/${id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url; link.download = `${contract?.title || "contract"}.pdf`; link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const handleAdvanceToApproval = async () => {
    if (!workflow?.id) return;
    try {
      await apiClient.post(`/governed-workflows/${workflow.id}/advance-to-approval`);
      toast.success("Advanced to approval stage. Approvers have been notified."); fetchContract();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to advance to approval"); }
  };

  const handleCreateWorkflow = async () => {
    if (selectedReviewers.length === 0 && externalReviewers.length === 0) { toast.error("Please select at least one reviewer (internal or external)"); return; }
    if (selectedApprovers.length === 0) { toast.error("Please select at least one approver"); return; }
    if (selectedReviewers.filter((r) => selectedApprovers.includes(r)).length > 0) { toast.error("A user cannot be both reviewer and approver (segregation of duty)"); return; }
    try {
      const payload = { contract_id: id, reviewers: selectedReviewers, approvers: selectedApprovers };
      if (externalReviewers.length > 0) payload.external_reviewers = externalReviewers;
      if (workflowDueDate) payload.due_date = workflowDueDate;
      await apiClient.post("/governed-workflows", payload);
      toast.success("Contract submitted for review");
      setWorkflowDialogOpen(false); setSelectedReviewers([]); setSelectedApprovers([]); setExternalReviewers([]); setWorkflowDueDate(""); setPolicySuggestions(null); fetchContract();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create workflow"); }
  };

  const fetchPolicySuggestions = async () => {
    try {
      const res = await apiClient.get(`/approval-policies/suggest/${id}`);
      setPolicySuggestions(res.data);
      if (res.data.suggested_reviewers?.length > 0 && selectedReviewers.length === 0) setSelectedReviewers(res.data.suggested_reviewers.filter((r) => r.id !== user?.id).map((r) => r.id));
      if (res.data.suggested_approvers?.length > 0 && selectedApprovers.length === 0) setSelectedApprovers(res.data.suggested_approvers.filter((a) => a.id !== user?.id).map((a) => a.id));
    } catch { /* ok */ }
  };

  const openActionDialog = (type) => { setActionType(type); setActionComment(""); setActionDialogOpen(true); };

  const handleWorkflowAction = async () => {
    if (!workflow) return;
    try {
      if (actionType === "review-approve") await apiClient.post(`/governed-workflows/${workflow.id}/review-approve${actionComment ? `?comment=${encodeURIComponent(actionComment)}` : ""}`);
      else if (actionType === "send-back") await apiClient.post(`/governed-workflows/${workflow.id}/send-back?reason=${encodeURIComponent(actionComment || "Revision needed")}`);
      else if (actionType === "approve") await apiClient.post(`/governed-workflows/${workflow.id}/approve${actionComment ? `?comment=${encodeURIComponent(actionComment)}` : ""}`);
      else if (actionType === "reject") await apiClient.post(`/governed-workflows/${workflow.id}/reject?comment=${encodeURIComponent(actionComment || "Rejected")}`);
      toast.success(actionType === "review-approve" ? "Review approved" : actionType === "send-back" ? "Sent back for revision" : actionType === "approve" ? "Contract approved" : "Contract rejected");
      setActionDialogOpen(false); fetchContract();
    } catch (error) { toast.error(error.response?.data?.detail || "Action failed"); }
  };

  const handleCancelWorkflow = async () => {
    if (!workflow || !window.confirm("Recall this workflow?")) return;
    try { await apiClient.post(`/governed-workflows/${workflow.id}/cancel`); toast.success("Workflow cancelled"); fetchContract(); }
    catch (error) { toast.error(error.response?.data?.detail || "Failed to cancel"); }
  };

  const handleResubmitForReview = async () => {
    if (!workflow) return;
    try { await apiClient.post(`/governed-workflows/${workflow.id}/resubmit-review`); toast.success("Resubmitted for review"); fetchContract(); }
    catch (error) { toast.error(error.response?.data?.detail || "Failed to resubmit"); }
  };

  const handleRecordExternalFeedback = async (reviewerId, data) => {
    if (!workflow) return;
    try {
      await apiClient.post(`/governed-workflows/${workflow.id}/external-review/${reviewerId}`, data);
      toast.success("External reviewer feedback recorded");
      fetchContract();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to record feedback"); }
  };

  const handleCreateAddendum = async () => {
    if (!addendumForm.title || !addendumForm.description) { toast.error("Title and description are required"); return; }
    setAddendumSaving(true);
    try {
      const params = new URLSearchParams({ title: addendumForm.title, addendum_type: addendumForm.addendum_type, description: addendumForm.description, changes_summary: addendumForm.changes_summary });
      if (addendumForm.effective_date) params.append("effective_date", addendumForm.effective_date);
      if (addendumForm.new_expiration_date) params.append("new_expiration_date", addendumForm.new_expiration_date);
      if (addendumForm.new_value) params.append("new_value", addendumForm.new_value);
      if (addendumForm.template_id) params.append("template_id", addendumForm.template_id);
      if (addendumForm.sections && addendumForm.sections.length > 0) {
        params.append("sections", JSON.stringify(addendumForm.sections));
        const content = addendumForm.sections.map((s) => `## ${s.name}\n\n${s.content}`).join("\n\n---\n\n");
        params.append("content", content);
      }
      await apiClient.post(`/contracts/${id}/addenda?${params.toString()}`);
      toast.success("Addendum created successfully");
      setAddendumDialogOpen(false); setAddendumForm({ addendum_type: "amendment", title: "", description: "", changes_summary: "", effective_date: "", new_expiration_date: "", new_value: "", template_id: null, sections: null });
      fetchContract(); fetchAddenda();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create addendum"); }
    finally { setAddendumSaving(false); }
  };

  const handleSubmitAddendumWorkflow = async () => {
    if (!selectedAddendumId || addendumReviewers.length === 0 || addendumApprovers.length === 0) {
      toast.error("Select at least one reviewer and one approver"); return;
    }
    try {
      await apiClient.post("/addendum-workflows", { addendum_id: selectedAddendumId, reviewers: addendumReviewers, approvers: addendumApprovers });
      toast.success("Addendum submitted for review");
      setAddendumWorkflowDialogOpen(false); setAddendumReviewers([]); setAddendumApprovers([]); setSelectedAddendumId(null); fetchAddenda();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit addendum"); }
  };

  const handleAddendumWorkflowAction = async (workflowId, action, comment = "") => {
    try {
      const params = comment ? `?comment=${encodeURIComponent(comment)}` : action === "send-back" ? "?reason=Needs revision" : "";
      await apiClient.post(`/addendum-workflows/${workflowId}/${action}${params}`);
      toast.success(`Addendum ${action.replace("-", " ")} successful`); fetchAddenda();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const openShareDraft = () => {
    setShareDraftData({
      recipient_email: contract?.contracting_party?.contact_email || "",
      recipient_name: contract?.contracting_party?.legal_name || "",
      message: "",
    });
    setShareDraftDialogOpen(true);
  };

  const canReview = workflow?.stage === "review" && workflow?.review_steps?.some((s) => s.user_id === user?.id && s.status === "pending");
  const canApprove = workflow?.stage === "approval" && workflow?.approval_steps?.some((s) => s.user_id === user?.id && s.status === "pending");
  const accessLevel = contract?._access_level || "full";
  const isReadOnly = accessLevel === "read_only";

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  if (!contract) return (
    <div className="text-center py-20">
      <p className="text-slate-500">Contract not found</p>
      <Link to="/contracts"><Button variant="link">Back to Contracts</Button></Link>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contract-detail-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/contracts")} className="rounded-sm mt-1" data-testid="back-btn"><ArrowLeft size={20} /></Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{contract.title}</h1>
              <Badge className={`rounded-sm ${statusColors[contract.status]}`}>{statusLabels[contract.status] || contract.status}</Badge>
            </div>
            <p className="text-slate-500">{contract.contracting_party?.legal_name || "No supplier specified"}</p>
          </div>
          {(user?.role === "superuser" || user?.role === "admin") && (
            <Link to={`/audit-log?contract=${id}`}>
              <Button variant="outline" size="sm" className="gap-2 rounded-sm" data-testid="view-audit-trail-btn">
                <ScrollText size={14} /> Audit Trail
              </Button>
            </Link>
          )}
        </div>
        {!isReadOnly && (
          <ContractHeader
            contract={contract} workflow={workflow} user={user} id={id}
            canReview={canReview} canApprove={canApprove}
            esignConfigured={esignConfigured}
            onSignatureDialog={() => setSignatureDialogOpen(true)}
            onWorkflowDialog={() => { setWorkflowDialogOpen(true); fetchPolicySuggestions(); }}
            onAddendumDialog={() => setAddendumDialogOpen(true)}
            onDeleteDialog={() => setDeleteDialogOpen(true)}
            onArchive={handleArchive} onSign={handleSign}
            onShareDraft={openShareDraft}
            onDownloadPdf={handleDownloadPdf}
            onPrintSign={() => setPrintSignDialogOpen(true)}
            onCancelWorkflow={handleCancelWorkflow} onResubmit={handleResubmitForReview}
            openActionDialog={openActionDialog}
          />
        )}
        {isReadOnly && (
          <Badge className="rounded-sm bg-amber-100 text-amber-700 text-xs px-3 py-1.5" data-testid="read-only-badge">
            Read-Only Access
          </Badge>
        )}
      </div>

      {/* Lifecycle Progress Bar */}
      <Card className="px-6 py-4">
        <LifecycleProgressBar contract={contract} workflow={workflow} />
      </Card>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="addenda" data-testid="addenda-tab">Addenda ({addenda.length})</TabsTrigger>
              <TabsTrigger value="notices" data-testid="notices-tab-trigger">Notices</TabsTrigger>
              <TabsTrigger value="history">Version History</TabsTrigger>
              <TabsTrigger value="workflow">Workflow</TabsTrigger>
              <TabsTrigger value="comments" data-testid="comments-tab-trigger">
                <MessageSquare size={14} className="mr-1" />Comments
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="documents-tab-trigger">
                <Paperclip size={14} className="mr-1" />Documents
              </TabsTrigger>
            </TabsList>

            <TabsContent value="content">
              <Card><CardContent className="pt-6">
                <div className="editor-container min-h-[400px]">
                  {contract.content ? (
                    <div className="contract-content whitespace-pre-wrap">{contract.content}</div>
                  ) : (
                    <div className="text-center py-20 text-slate-400">
                      <FileText size={48} className="mx-auto mb-4 opacity-50" /><p>No content yet</p>
                      <Link to={`/contracts/${id}/edit`}><Button variant="link" className="text-cyan-600 mt-2">Add content</Button></Link>
                    </div>
                  )}
                </div>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="addenda">
              <Card><CardContent className="pt-6">
                <AddendaTab
                  addenda={addenda}
                  addendumWorkflows={addendumWorkflows}
                  user={user}
                  contractId={id}
                  onCreateAddendum={() => setAddendumDialogOpen(true)}
                  onPreviewAddendum={setPreviewAddendum}
                  onSubmitForReview={(addendumId) => { setSelectedAddendumId(addendumId); setAddendumWorkflowDialogOpen(true); }}
                  onWorkflowAction={handleAddendumWorkflowAction}
                />
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="notices">
              <NoticesTab contract={contract} users={users} />
            </TabsContent>

            <TabsContent value="history">
              <Card><CardContent className="pt-6">
                {contract.versions?.length > 0 ? (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-4">
                      {contract.versions.slice().reverse().map((version, index, reversedArr) => {
                        const prevVersion = reversedArr[index + 1];
                        return (
                          <div key={version.version || index} className="flex items-start gap-4 p-4 border border-slate-200 dark:border-slate-700 rounded-sm" data-testid={`version-entry-${version.version}`}>
                            <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                              <History size={18} className="text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">Version {version.version}</span>
                                <div className="flex items-center gap-2">
                                  {prevVersion && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs rounded-sm gap-1.5"
                                      data-testid={`compare-btn-${version.version}`}
                                      onClick={() => setCompareVersions({ old: prevVersion, new: version })}
                                    >
                                      <GitCompareArrows size={12} /> Compare with v{prevVersion.version}
                                    </Button>
                                  )}
                                  <span className="text-xs text-slate-500">{formatDateTime(version.modified_at)}</span>
                                </div>
                              </div>
                              <p className="text-sm text-slate-500">{version.comment || "No comment"}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-20 text-slate-400"><History size={48} className="mx-auto mb-4 opacity-50" /><p>No version history</p></div>
                )}
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="workflow">
              <Card><CardContent className="pt-6">
                <WorkflowStepper
                  workflow={workflow} contract={contract} user={user}
                  canReview={canReview} canApprove={canApprove}
                  openActionDialog={openActionDialog}
                  onCancelWorkflow={handleCancelWorkflow}
                  onResubmit={handleResubmitForReview}
                  onSubmitForReview={() => { setWorkflowDialogOpen(true); fetchPolicySuggestions(); }}
                  onShareDraft={openShareDraft}
                  onAdvanceToApproval={handleAdvanceToApproval}
                  onRecordExternalFeedback={handleRecordExternalFeedback}
                  onViewChanges={contract.versions?.length >= 2 ? () => {
                    const sorted = [...contract.versions].sort((a, b) => a.version - b.version);
                    setCompareVersions({ old: sorted[sorted.length - 2], new: sorted[sorted.length - 1] });
                  } : undefined}
                />
                <ExecutiveSummaryCard executiveSummary={executiveSummary} workflow={workflow} contract={contract} />
              </CardContent></Card>
            </TabsContent>
            <TabsContent value="comments">
              <Card><CardContent className="pt-6">
                <CommentsPanel contractId={id} sections={contract?.sections || []} />
              </CardContent></Card>
            </TabsContent>
            <TabsContent value="documents">
              <Card><CardContent className="pt-6">
                <DocumentsGallery contractId={id} />
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <ContractSidebar contract={contract} />
          <AccessControlPanel contract={contract} users={users} onRefresh={fetchContract} />
        </div>
      </div>

      {/* Dialogs */}
      <SignatureDialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen} signatureData={signatureData} setSignatureData={setSignatureData} onSubmit={handleRequestSignature} />
      <ShareDraftDialog open={shareDraftDialogOpen} onOpenChange={setShareDraftDialogOpen} data={shareDraftData} setData={setShareDraftData} onSubmit={handleShareDraft} />
      <WorkflowDialog
        open={workflowDialogOpen} onOpenChange={setWorkflowDialogOpen} users={users} user={user} policySuggestions={policySuggestions}
        selectedReviewers={selectedReviewers} setSelectedReviewers={setSelectedReviewers}
        selectedApprovers={selectedApprovers} setSelectedApprovers={setSelectedApprovers}
        externalReviewers={externalReviewers} setExternalReviewers={setExternalReviewers}
        workflowDueDate={workflowDueDate} setWorkflowDueDate={setWorkflowDueDate}
        onSubmit={handleCreateWorkflow} toast={toast} contractCreatorId={contract?.created_by}
      />
      <ActionDialog open={actionDialogOpen} onOpenChange={setActionDialogOpen} actionType={actionType} actionComment={actionComment} setActionComment={setActionComment} onSubmit={handleWorkflowAction} />
      <AddendumDialog open={addendumDialogOpen} onOpenChange={setAddendumDialogOpen} form={addendumForm} setForm={setAddendumForm} onSubmit={handleCreateAddendum} saving={addendumSaving} contract={contract} />
      <DeleteContractDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} contract={contract} onDelete={handleDelete} />
      <AddendumWorkflowDialog
        open={addendumWorkflowDialogOpen} onOpenChange={setAddendumWorkflowDialogOpen}
        users={users} user={user}
        reviewers={addendumReviewers} setReviewers={setAddendumReviewers}
        approvers={addendumApprovers} setApprovers={setAddendumApprovers}
        onSubmit={handleSubmitAddendumWorkflow}
      />
      <AddendumPreviewDialog previewAddendum={previewAddendum} onClose={() => setPreviewAddendum(null)} contract={contract} />
      <VersionCompareDialog
        open={!!compareVersions}
        onOpenChange={(open) => { if (!open) setCompareVersions(null); }}
        oldVersion={compareVersions?.old}
        newVersion={compareVersions?.new}
      />
      <PrintSignDialog
        open={printSignDialogOpen}
        onOpenChange={setPrintSignDialogOpen}
        contract={contract}
        user={user}
        onComplete={fetchContract}
      />
    </div>
  );
}
